import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { injectStyles } from "./injectStyles";

export type PositionType =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right"
  | "left-top"
  | "left-center"
  | "left-bottom"
  | "right-top"
  | "right-center"
  | "right-bottom"
  | "auto";

export type SpecificPositionType = Exclude<PositionType, "auto">;

export interface PopupMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  menuClassName?: string;
  position?: PositionType;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  animationDuration?: number;
  viewportPadding?: number;
  enableScroll?: boolean;
  noDefaultStyle?: boolean;
  theme?: 'dark' | 'light';
  hoverTrigger?: boolean;
  animation?: 'scale' | 'in' | 'out';
}

interface MenuItemProps {
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  children?: React.ReactNode;
}

export const PopupMenu: React.FC<PopupMenuProps> = ({
  trigger,
  children,
  header,
  footer,
  className = "",
  menuClassName = "",
  position = "auto",
  onOpenChange,
  onClose,
  animationDuration = 200,
  viewportPadding = 5,
  enableScroll = false,
  noDefaultStyle = false,
  theme = 'light',
  hoverTrigger = false,
  animation = 'scale',
}) => {
  const [open, setOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [calculatedPosition, setCalculatedPosition] = useState({
    top: 0,
    left: 0,
    direction: "bottom",
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disableScroll = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isOpen) {
      setOpen(true);
      setTimeout(() => {
        setIsVisible(true);
      }, 10);

      if (!enableScroll) {
        document.addEventListener("wheel", disableScroll, { passive: false });
        document.addEventListener("touchmove", disableScroll, { passive: false });
      }
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        onClose?.();

        if (!enableScroll) {
          document.removeEventListener("wheel", disableScroll);
          document.removeEventListener("touchmove", disableScroll);
        }
      }, animationDuration);
    }
    onOpenChange?.(isOpen);
  };

  const handleMouseEnter = () => {
    if (hoverTrigger) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      handleOpenChange(true);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTrigger) {
      hoverTimeoutRef.current = setTimeout(() => {
        if (open) {
          handleOpenChange(false);
        }
      }, 300);
    }
  };

  const handleMenuMouseEnter = () => {
    if (hoverTrigger) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  };

  const handleMenuMouseLeave = () => {
    if (hoverTrigger) {
      hoverTimeoutRef.current = setTimeout(() => {
        if (open) {
          handleOpenChange(false);
        }
      }, 150);
    }
  };

  const calculatePosition = useCallback(() => {
    if (!menuRef.current || !triggerRef.current) return;

    const menuRect = menuRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalTop = 0;
    let finalLeft = 0;
    let finalDirection = "bottom";

    const getPositionCoordinates = (pos: SpecificPositionType) => {
      const [direction, alignment] = pos.split("-") as [
        "top" | "bottom" | "left" | "right",
        "left" | "center" | "right" | "top" | "bottom"
      ];

      let menuLeft = 0;
      let menuTop = 0;

      switch (direction) {
        case "top":
          menuTop = triggerRect.top - menuRect.height - viewportPadding;
          break;
        case "bottom":
          menuTop = triggerRect.bottom + viewportPadding;
          break;
        case "left":
          menuLeft = triggerRect.left - menuRect.width - viewportPadding;
          break;
        case "right":
          menuLeft = triggerRect.right + viewportPadding;
          break;
      }

      switch (alignment) {
        case "left":
          menuLeft = triggerRect.left;
          break;
        case "right":
          menuLeft = triggerRect.right - menuRect.width;
          break;
        case "center":
          if (direction === "top" || direction === "bottom") {
            menuLeft = triggerRect.left + (triggerRect.width - menuRect.width) / 2;
          } else {
            menuTop = triggerRect.top + (triggerRect.height - menuRect.height) / 2;
          }
          break;
        case "top":
          menuTop = triggerRect.top;
          break;
        case "bottom":
          menuTop = triggerRect.bottom - menuRect.height;
          break;
      }

      return { top: menuTop, left: menuLeft };
    };

    const doesPositionFit = (pos: SpecificPositionType) => {
      const { top, left } = getPositionCoordinates(pos);
      return (
        left >= viewportPadding &&
        left + menuRect.width <= viewportWidth - viewportPadding &&
        top >= viewportPadding &&
        top + menuRect.height <= viewportHeight - viewportPadding
      );
    };

    const calculateOverflow = (pos: SpecificPositionType) => {
      const { top, left } = getPositionCoordinates(pos);
      const leftOverflow = Math.max(0, viewportPadding - left);
      const rightOverflow = Math.max(0, left + menuRect.width - (viewportWidth - viewportPadding));
      const topOverflow = Math.max(0, viewportPadding - top);
      const bottomOverflow = Math.max(0, top + menuRect.height - (viewportHeight - viewportPadding));
      return leftOverflow + rightOverflow + topOverflow + bottomOverflow;
    };

    const findBestPosition = () => {
      const preferredPositions: SpecificPositionType[] = [
        "bottom-left", "bottom-center", "bottom-right",
        "top-left", "top-center", "top-right",
        "right-top", "right-center", "right-bottom",
        "left-top", "left-center", "left-bottom",
      ];

      for (const pos of preferredPositions) {
        if (doesPositionFit(pos)) {
          return pos;
        }
      }

      let bestPosition: SpecificPositionType = "bottom-left";
      let minOverflow = Infinity;

      for (const pos of preferredPositions) {
        const overflow = calculateOverflow(pos);
        if (overflow < minOverflow) {
          minOverflow = overflow;
          bestPosition = pos;
        }
      }
      return bestPosition;
    };

    let bestPosition: SpecificPositionType;
    if (position === "auto") {
      bestPosition = findBestPosition();
    } else {
      bestPosition = position as SpecificPositionType;
      if (!doesPositionFit(bestPosition)) {
        bestPosition = findBestPosition();
      }
    }

    const { top, left } = getPositionCoordinates(bestPosition);
    finalTop = top;
    finalLeft = left;
    finalDirection = bestPosition.split("-")[0];

    setCalculatedPosition({ top: finalTop, left: finalLeft, direction: finalDirection });
  }, [position, viewportPadding]);

  useLayoutEffect(() => {
    injectStyles();

    if (open) {
      calculatePosition();
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node)
        ) {
          handleOpenChange(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", calculatePosition);

      if (!enableScroll) {
        window.addEventListener("scroll", calculatePosition, true);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("resize", calculatePosition);
        
        if (!enableScroll) {
          window.removeEventListener("scroll", calculatePosition, true);
        }

        if (!enableScroll) {
          document.removeEventListener("wheel", disableScroll);
          document.removeEventListener("touchmove", disableScroll);
        }
      };
    }
  }, [open, calculatePosition, disableScroll, enableScroll]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      document.removeEventListener("wheel", disableScroll);
      document.removeEventListener("touchmove", disableScroll);
    };
  }, [disableScroll]);

  const getAnimationClass = () => {
    if (animation === 'in') {
      const directionClass = `popup-menu-in-from-${calculatedPosition.direction}`;
      return isVisible ? `popup-menu-visible popup-menu-in` : directionClass;
    }
    if (animation === 'out') {
      const directionClass = `popup-menu-out-from-${calculatedPosition.direction}`;
      return isVisible ? `popup-menu-visible popup-menu-out` : directionClass;
    }
    
    const directionClass = `popup-menu-from-${calculatedPosition.direction}`;
    return isVisible ? `popup-menu-visible` : directionClass;
  };

  const combineClassNames = (...classes: (string | undefined)[]) => {
    return classes.filter(Boolean).join(" ");
  };

  const menu = open ? (
    <div
      ref={menuRef}
      className={combineClassNames(
        noDefaultStyle ? "" : "popup-menu",
        getAnimationClass(), 
        theme === 'dark' && !noDefaultStyle ? 'dark-theme' : '',
        menuClassName 
      )}
      role="menu"
      style={{
        top: calculatedPosition.top,
        left: calculatedPosition.left,
        transitionDuration: `${animationDuration}ms`,
        ...(noDefaultStyle ? {
          position: 'fixed',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${animationDuration}ms ease, transform ${animationDuration}ms ease`
        } : {})
      }}
      onMouseEnter={handleMenuMouseEnter}
      onMouseLeave={handleMenuMouseLeave}
    >
      {header && <div className={noDefaultStyle ? "" : "popup-menu-header"}>{header}</div>}
      <div className={noDefaultStyle ? "" : "popup-menu-content"}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement<MenuItemProps>(child)) {
            return React.cloneElement(child, {
              onClick: (e: React.MouseEvent) => {
                child.props.onClick?.(e);
                handleOpenChange(false);
              },
              className: combineClassNames(noDefaultStyle ? "" : "popup-menu-item", child.props.className || ""),
            });
          }
          return child;
        })}
      </div>
      {footer && <div className={noDefaultStyle ? "" : "popup-menu-footer"}>{footer}</div>}
    </div>
  ) : null;

  return (
    <div className={combineClassNames("popup-menu-wrapper", className)}>
      <div
        ref={triggerRef}
        onClick={() => !hoverTrigger && handleOpenChange(!open)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenChange(!open);
          }
        }}
        className="cursor-pointer"
      >
        {trigger}
      </div>
      {menu && createPortal(menu, document.body)}
    </div>
  );
};